"use client"
import { useCallback, useEffect, useRef } from 'react';

interface DebounceProps<T extends (...args: any[]) => void> {
    /** Delay in milliseconds @default 1000 */
    delay?: number;
    /** callback function to call when debounce delay ends */
    callback: T,
    /** allows callback call on mount @default false */
    triggerOnMount?: boolean
}

/** debounce a function after X milliseconds */
function useDebounce<T extends (...args: any[]) => void>({
    delay = 1000,
    callback,
}: DebounceProps<T>) {
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    /** debounce-er function - multiple calls within delay will delay the function call */
    const debouncedFn = useCallback(
        (...args: Parameters<T>) => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            timeoutRef.current = setTimeout(() => {
                callback(...args);
            }, delay);
        },
        [callback, delay]
    );

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    return debouncedFn;
}

export default useDebounce;
